import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import type {InsightGeoMapV4} from "../../models/crm-v4";
import {useInsightsFormatters} from "../../kit/insightsFormat";
import {InsightsTooltip} from "../../kit/InsightsTooltip";
import {targetProgressTone} from "../../kit/StatCard";
import GuestMap, {type GuestMapCountryValue, type GuestMapLocation} from "./GuestMap";

const MAP_FULLSCREEN_CONTAINER_ID = "crm-guest-origin-map";

interface InsightGuestMapCardProps {
    map?: InsightGeoMapV4;
    loading: boolean;
    comparing: boolean;
}

function DisplayedGuests({label, mapped, total}: { label: string; mapped: number; total?: number }) {
    const {t} = useTranslation();
    const {count, percent} = useInsightsFormatters();

    /*
        Only guests with a resolved home city reach the map, so the mapped count on its own reads as
        the period's guest count and quietly understates it. The total and the share it covers turn
        that into a figure you can judge - 20 guests means nothing until you know whether it is most
        of the period or a rounding error. Both are dropped once nothing is missing: "1,234 of 1,234
        (100 %)" is noise, and a total below the mapped count would be nonsense to print.
    */
    const isPartial = total != null && total > mapped;

    return (
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={700} color="text.primary">
                {isPartial
                    ? t("{{mapped}} of {{total}} guests ({{share}})", {
                        mapped: count(mapped),
                        total: count(total),
                        share: percent(mapped * 100 / total),
                    })
                    : `${count(mapped)} ${t("guests")}`}
            </Typography>
        </Stack>
    );
}

function CountryComparisonLegend() {
    const {t} = useTranslation();
    const items = [
        {tone: "success", label: t("Same or more guests")},
        {tone: "warning", label: t("Up to 20% fewer")},
        {tone: "error", label: t("More than 20% fewer")},
    ] as const;

    return (
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {t("Country change")}
            </Typography>
            {items.map(item => (
                <Stack key={item.tone} direction="row" alignItems="center" gap={0.75}>
                    <Box sx={{width: 10, height: 10, borderRadius: 0.75, bgcolor: `${item.tone}.main`}}/>
                    <Typography variant="body2" color="text.secondary">
                        {item.label}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    );
}

export function InsightGuestMapCard({map, loading, comparing}: InsightGuestMapCardProps) {
    const {t} = useTranslation();
    const propertyLocation = useMemo<GuestMapLocation | undefined>(() => {
        const latitude = map?.propertyLocation?.lat;
        const longitude = map?.propertyLocation?.lng;
        return Number.isFinite(latitude) && Number.isFinite(longitude)
            ? {latitude: latitude as number, longitude: longitude as number}
            : undefined;
    }, [map?.propertyLocation?.lat, map?.propertyLocation?.lng]);
    const countryValues = useMemo<GuestMapCountryValue[]>(() => {
        const totals = new Map<string, { selected: number; comparison: number }>();
        for (const point of map?.points ?? []) {
            if (!point.countryCode) continue;
            const countryCode = point.countryCode.toUpperCase();
            const total = totals.get(countryCode) ?? {selected: 0, comparison: 0};
            total.selected += point.guests;
            total.comparison += point.compareGuests ?? 0;
            totals.set(countryCode, total);
        }

        return Array.from(totals.entries()).flatMap(([countryCode, total]) => {
            if (total.selected === 0 && (!comparing || total.comparison === 0)) return [];

            if (!comparing) {
                return [{
                    countryCode,
                    selectedGuests: total.selected,
                }];
            }

            const progress = total.comparison > 0
                ? total.selected * 100 / total.comparison
                : 100;

            return [{
                countryCode,
                selectedGuests: total.selected,
                comparisonGuests: total.comparison,
                tone: targetProgressTone({
                    progress,
                    achieved: total.selected >= total.comparison,
                }),
            }];
        });
    }, [comparing, map?.points]);

    const title = t("Guest origins");
    const hint = t("City-level locations of room-stay guests in the selected period; only guests with a resolved home city appear on the map");

    return (
        <Box component={"section"} aria-label={title} aria-busy={loading} sx={{gridColumn: "1 / -1", minWidth: 0}}>
                <Box
                    id={MAP_FULLSCREEN_CONTAINER_ID}
                    sx={{
                        position: "relative",
                        "&:fullscreen": {
                            bgcolor: "background.paper",
                        },
                        "&:fullscreen .guest-map-root": {
                            height: "100%",
                            borderRadius: 0,
                        },
                    }}
                >
                    <GuestMap
                        points={map?.points ?? []}
                        loading={loading}
                        height={800}
                        countryValues={countryValues}
                        selectedPeriod={comparing ? {
                            label: t("Selected period"),
                        } : undefined}
                        comparisonPeriod={comparing ? {
                            label: t("Comparison period"),
                        } : undefined}
                        propertyLocation={propertyLocation}
                        fullscreenContainerId={MAP_FULLSCREEN_CONTAINER_ID}
                    />
                    {!loading && map && (
                        <Box
                            sx={{
                                position: "absolute",
                                zIndex: 10,
                                top: {xs: 8, sm: 12},
                                left: {xs: 8, sm: 12},
                                maxWidth: {xs: "calc(100% - 24px)", md: "calc(100% - 72px)"},
                                pointerEvents: "none",
                                overflow: "hidden",
                                px: {xs: 1, sm: 1.25},
                                py: {xs: 0.75, sm: 1},
                                borderRadius: 2,
                                border: 1,
                                borderColor: "divider",
                                bgcolor: "background.paper",
                            }}
                        >
                            <Stack gap={comparing && countryValues.length > 0 ? 1.25 : 0}>
                                <Stack
                                    direction={{xs: "column", sm: "row"}}
                                    alignItems={{xs: "flex-start", sm: "center"}}
                                    gap={{xs: 0.5, sm: 2.5}}
                                >
                                    <DisplayedGuests
                                        label={comparing ? t("Selected period") : t("Displayed")}
                                        mapped={map.mappedGuests}
                                        total={map.totalGuests}
                                    />
                                    {comparing && map.compareMappedGuests != null && (
                                        <DisplayedGuests
                                            label={t("Comparison period")}
                                            mapped={map.compareMappedGuests}
                                            total={map.compareTotalGuests}
                                        />
                                    )}
                                </Stack>
                                {comparing && countryValues.length > 0 && <CountryComparisonLegend/>}
                            </Stack>
                        </Box>
                    )}
                </Box>

                <Stack direction={"row"} alignItems={"center"} gap={0.25} sx={{mt: 1.5, minWidth: 0}}>
                    <Typography variant={"body1"} color={"text.secondary"} fontWeight={600} noWrap>
                        {title}
                    </Typography>

                    <InsightsTooltip placement={"top"} title={hint}>
                        <IconButton
                            size={"small"}
                            aria-label={`${title} information`}
                            sx={{color: "text.disabled", flexShrink: 0, p: 0.5}}
                        >
                            <InfoOutlinedIcon sx={{fontSize: 18}}/>
                        </IconButton>
                    </InsightsTooltip>
                </Stack>
        </Box>
    );
}

export default InsightGuestMapCard;
